import { scopeLevelSchema } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { paginated, paginationInputSchema } from './common/pagination.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

/**
 * A machine that may write over a signed webhook, as a screen shows it —
 * docs/decisions/0065. **There is no `sealedMaterial` and no secret here, and
 * there is no route that returns one**: `.claude/rules/secrets.md` — a key is
 * replaced, never displayed. `fingerprint` is the last four characters, which is
 * what lets a person check that the value in n8n is the value here without
 * either of them ever being sent.
 */
export const integrationClientSchema = z.object({
  id: z.uuid(),
  /** Opaque, and in the endpoint's own path, so it is safe to show. */
  clientRef: z.string(),
  name: z.string(),
  scopeLevel: scopeLevelSchema,
  scopeId: z.uuid().nullable(),
  scopeName: z.string().nullable(),
  fingerprint: z.string(),
  keyId: z.string(),
  isActive: z.boolean(),
  configuredByUserId: z.uuid(),
  configuredByName: z.string().nullable(),
  configuredAt: z.string(),
  createdAt: z.string(),
});

export type IntegrationClient = z.infer<typeof integrationClientSchema>;

const nameSchema = z.string().trim().min(2).max(120);

/**
 * The one moment a signing secret exists outside the database. It is answered
 * **once**, on the response of the call that minted it, and is never stored
 * unsealed, listed or re-readable — the same rule the invitation link follows
 * (`userContract.create`). A second copy would mean a second key, which is a
 * rotation, which is what `rotateSecret` is for.
 */
const mintedSchema = z.object({
  client: integrationClientSchema,
  /** Show it once, then never again. */
  signingSecret: z.string(),
});

export const integrationClientContract = {
  list: oc
    .route({
      method: 'GET',
      path: '/integration-clients',
      summary: 'Integration clients and their key fingerprints',
    })
    .errors(guarded)
    .input(
      paginationInputSchema.extend({
        status: z.enum(['all', 'active', 'inactive']).default('all'),
      }),
    )
    .output(paginated(integrationClientSchema)),

  create: oc
    .route({ method: 'POST', path: '/integration-clients' })
    .errors(guarded)
    .input(
      z.object({
        name: nameSchema,
        scopeLevel: scopeLevelSchema,
        /** Null only for a global client; the database refuses the other pairings. */
        scopeId: z.uuid().nullable().default(null),
      }),
    )
    .output(mintedSchema),

  /**
   * A new signing secret for a client that already exists. The old one stops
   * being accepted the moment this commits — there is deliberately no overlap
   * window, because the flow is one place and pasting the new value into it is
   * one edit; an overlap would mean a compromised key kept working.
   */
  rotateSecret: oc
    .route({ method: 'POST', path: '/integration-clients/{id}/signing-secret' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(mintedSchema),

  /**
   * Off rather than deleted. A client that has entered donations is named by
   * their audit rows, and its endpoint answers a deactivated client exactly as it
   * answers an unknown one.
   */
  setActive: oc
    .route({ method: 'POST', path: '/integration-clients/{id}/activation' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), isActive: z.boolean() }))
    .output(z.object({ client: integrationClientSchema })),
};
